import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectEmployeeInformation,
  updateEsiField,
  addNominee,
  updateNomineeField,
  setNomineeImage,
  removeNominee,
  removeNomineeImage,
} from "../../../Redux/employeeInformationSlice.js";
import styles from "./EsiForm.module.scss";

const EsiForm = forwardRef(function EsiForm(_, ref) {
  const dispatch = useDispatch();

  const { esiDetails, basicInformation, addressDetails, bankDetails } =
    useSelector(selectEmployeeInformation);

  const nominees = esiDetails.nominees;

  const [errorFields, setErrorFields] = useState(new Set());
  const [nomineeErrors, setNomineeErrors] = useState({});

  const clearError = (name) => {
    setErrorFields((prev) => {
      const next = new Set(prev);
      next.delete(name);
      return next;
    });
  };

  const clearNomineeError = (id, name) => {
    setNomineeErrors((prev) => {
      const entryErrors = new Set(prev[id]);
      entryErrors.delete(name);
      return { ...prev, [id]: entryErrors };
    });
  };

  const updateField = (name, value) => {
    dispatch(updateEsiField({ name, value }));
    clearError(name);
  };

  const updateNominee = (id, name, value) => {
    dispatch(updateNomineeField({ id, name, value }));
    clearNomineeError(id, name);
  };

  const handleImage = (id, file) => {
    if (!file || file.size > 10 * 1024 * 1024) return;

    dispatch(
      setNomineeImage({
        id,
        image: {
          name: file.name,
          url: URL.createObjectURL(file),
        },
      }),
    );
  };

  const PF_OPTIONS = [100, 75, 50, 25, 0];

  const getAvailableOptions = (currentId) => {
    const totalExceptCurrent = nominees.reduce((sum, n) => {
      if (n.id === currentId) return sum;
      return sum + Number(n.share || 0);
    }, 0);

    const remaining = 100 - totalExceptCurrent;
    return PF_OPTIONS.filter((opt) => opt <= remaining);
  };

  const canAddNominee = () => {
    const last = nominees[nominees.length - 1];
    if (nominees.length === 1 && !last.name) return true;
    return (
      last.name && last.address && last.relationship && last.share && last.dob
    );
  };

  const totalShare = nominees.reduce((sum, n) => sum + Number(n.share || 0), 0);

  const getMissingFields = useCallback(() => {
    const missing = [];
    const missingNames = new Set();
    const newNomineeErrors = {};

    const hasNomineeData = nominees.some(
      (n) => n.name || n.address || n.relationship || n.share || n.dob,
    );

    if (hasNomineeData) {
      nominees.forEach((n, i) => {
        const nomineeFieldErrors = new Set();

        if (!n.name) {
          missing.push(`Nominee ${i + 1} Name`);
          nomineeFieldErrors.add("name");
        }
        if (!n.address) {
          missing.push(`Nominee ${i + 1} Address`);
          nomineeFieldErrors.add("address");
        }
        if (!n.relationship) {
          missing.push(`Nominee ${i + 1} Relationship`);
          nomineeFieldErrors.add("relationship");
        }
        if (!n.share) {
          missing.push(`Nominee ${i + 1} Share`);
          nomineeFieldErrors.add("share");
        }
        if (!n.dob) {
          missing.push(`Nominee ${i + 1} DOB`);
          nomineeFieldErrors.add("dob");
        }

        newNomineeErrors[n.id] = nomineeFieldErrors;
      });
    }

    if (!esiDetails.attendance_cycle) {
      missing.push("Attendance Cycle");
      missingNames.add("attendance_cycle");
    }

    if (!esiDetails.contract_duration) {
      missing.push("Contract Duration");
      missingNames.add("contract_duration");
    }

    if (!esiDetails.contract_end_date) {
      missing.push("Contract End Date");
      missingNames.add("contract_end_date");
    }

    if (!esiDetails.notice_period_before) {
      missing.push("Notice Before Confirmation");
      missingNames.add("notice_period_before");
    }

    if (!esiDetails.notice_period) {
      missing.push("Notice After Confirmation");
      missingNames.add("notice_period");
    }

    if (!esiDetails.gender) {
      missing.push("Gender");
      missingNames.add("gender");
    }

    setErrorFields(missingNames);
    setNomineeErrors(newNomineeErrors);

    return missing;
  }, [nominees, totalShare, esiDetails]);

  const handleRemoveImage = (id) => {
    const nominee = nominees.find((n) => n.id === id);
    if (nominee?.image?.url) {
      URL.revokeObjectURL(nominee.image.url);
    }
    dispatch(removeNomineeImage({ id }));
  };

  useImperativeHandle(ref, () => ({
    getMissingFields,
    isComplete: () => getMissingFields().length === 0,
  }));

  const hasNomineeError = (id, name) => nomineeErrors[id]?.has(name) ?? false;

  return (
    <div className={styles.esi}>
      <header className={styles.sectionHeader}>ESI Form</header>

      <div className={styles.content}>
        <div className={styles.formGrid}>
          <label className={styles.field}>
            <span>
              Name<b>*</b>
            </span>
            <input value={basicInformation.first_name} disabled />
          </label>

          <label className={styles.field}>
            <span>
              Relation<b>*</b>
              <span>
                <div className={styles.inlineOptions}>
                  {["Father", "Husband"].map((v) => (
                    <label key={v}>
                      <input
                        type="radio"
                        name="relationType"
                        checked={esiDetails.NameOfFamliy === v}
                        onChange={() => {
                          updateField("NameOfFamliy", v);
                          updateField("FatherORHUSBANDNAME", "");
                        }}
                      />
                      {v}
                    </label>
                  ))}
                </div>
              </span>
            </span>
            <input
              className={styles.relationInput}
              placeholder={`Enter ${esiDetails.NameOfFamliy} Name`}
              value={esiDetails.FatherORHUSBANDNAME || ""}
              onChange={(e) =>
                updateField("FatherORHUSBANDNAME", e.target.value)
              }
            />
          </label>

          <label className={styles.field}>
            <span>Date of Birth</span>
            <input value={basicInformation.DateofBirth} disabled />
          </label>

          <label className={styles.field}>
            <span>
              Gender<b>*</b>
            </span>
            <div className={styles.inlineOptions}>
              {["Male", "Female", "Other"].map((g) => (
                <label key={g}>
                  <input
                    type="radio"
                    name="gender"
                    checked={esiDetails.gender === g}
                    onChange={() => updateField("gender", g)}
                  />
                  {g}
                </label>
              ))}
            </div>
            {errorFields.has("gender") && (
              <p className={styles.fieldError}>Please select a gender</p>
            )}
          </label>

          <label className={styles.field}>
            <span>Marital Status</span>
            <select
              value={esiDetails.marital_status || ""}
              onChange={(e) => updateField("marital_status", e.target.value)}
            >
              <option value="">Select</option>
              <option>UNMARRIED</option>
              <option>MARRIED</option>
              <option>OTHERS</option>
            </select>
          </label>

          <label className={styles.field}>
            <span>Account Number</span>
            <input value={bankDetails.account_number} disabled />
          </label>

          <label className={styles.field}>
            <span>Address Permanent</span>
            <input value={addressDetails.permanentStreet} disabled />
          </label>

          <label className={styles.field}>
            <span>Address Temporary</span>
            <input value={addressDetails.presentStreet} disabled />
          </label>

          <label className={styles.field}>
            <span>
              Date of Joining<b>*</b>
            </span>
            <input
              type="date"
              value={esiDetails.date_of_joining || ""}
              onChange={(e) => updateField("date_of_joining", e.target.value)}
            />
          </label>

          <label className={styles.field}>
            <span>E.P.F</span>
            <input
              value={esiDetails.e_p_f}
              onChange={(e) => updateField("e_p_f", e.target.value)}
            />
          </label>

          <label className={styles.field}>
            <span>E.P.S</span>
            <input
              value={esiDetails.e_p_s}
              onChange={(e) => updateField("e_p_s", e.target.value)}
            />
          </label>

          <label className={styles.field}>
            <span>ESI Number</span>
            <input
              value={esiDetails.previous_ESI_number || ""}
              onChange={(e) =>
                updateField("previous_ESI_number", e.target.value)
              }
            />
          </label>

          <label className={styles.field}>
            <span>ESI Dispensary</span>
            <input
              value={esiDetails.esi_dispensary || ""}
              onChange={(e) => updateField("esi_dispensary", e.target.value)}
            />
          </label>

          <label className={styles.field}>
            <span>
              Attendance Cycle<b>*</b>
            </span>
            <select
              value={esiDetails.attendance_cycle || ""}
              className={
                errorFields.has("attendance_cycle") ? styles.inputError : ""
              }
              onChange={(e) => updateField("attendance_cycle", e.target.value)}
            >
              <option value="">Select</option>
              <option>1 - 25</option>
              <option>26 - 25</option>
              <option>26 - 31</option>
            </select>
            {errorFields.has("attendance_cycle") && (
              <p className={styles.fieldError}>Please select an option</p>
            )}
          </label>

          <label className={styles.field}>
            <span>
              Contract Duration<b>*</b>
            </span>
            <select
              value={esiDetails.contract_duration || ""}
              className={
                errorFields.has("contract_duration") ? styles.inputError : ""
              }
              onChange={(e) => updateField("contract_duration", e.target.value)}
            >
              <option value="">Select</option>
              <option>3</option>
              <option>6</option>
              <option>12</option>
            </select>
            {errorFields.has("contract_duration") && (
              <p className={styles.fieldError}>Please select an option</p>
            )}
          </label>

          <label className={styles.field}>
            <span>
              Contract End Date<b>*</b>
            </span>
            <input
              type="date"
              value={esiDetails.contract_end_date || ""}
              className={
                errorFields.has("contract_end_date") ? styles.inputError : ""
              }
              onChange={(e) => updateField("contract_end_date", e.target.value)}
            />
            {errorFields.has("contract_end_date") && (
              <p className={styles.fieldError}>Please select a date</p>
            )}
          </label>

          <label className={styles.field}>
            <span>
              Notice Period (Before Confirmation)<b>*</b>
            </span>
            <select
              value={esiDetails.notice_period_before || ""}
              className={
                errorFields.has("notice_period_before") ? styles.inputError : ""
              }
              onChange={(e) =>
                updateField("notice_period_before", e.target.value)
              }
            >
              <option value="">Select</option>
              <option>7</option>
              <option>15</option>
              <option>30</option>
            </select>
            {errorFields.has("notice_period_before") && (
              <p className={styles.fieldError}>Please select an option</p>
            )}
          </label>

          <label className={styles.field}>
            <span>
              Notice Period (After Confirmation)<b>*</b>
            </span>
            <select
              value={esiDetails.notice_period || ""}
              className={
                errorFields.has("notice_period") ? styles.inputError : ""
              }
              onChange={(e) => updateField("notice_period", e.target.value)}
            >
              <option value="">Select</option>
              <option>7</option>
              <option>15</option>
              <option>30</option>
            </select>
            {errorFields.has("notice_period") && (
              <p className={styles.fieldError}>Please select an option</p>
            )}
          </label>
        </div>

        <div className={styles.nomineeHeader}>
          <h3>PART-A (E.P.F)</h3>
          <button
            className={styles.addButton}
            disabled={!canAddNominee() || nominees.length >= 4}
            onClick={() => dispatch(addNominee())}
          >
            Add Nominee
          </button>
        </div>

        {nominees.map((n, index) => (
          <div key={n.id} className={styles.nomineeSection}>
            {index > 0 && (
              <button
                className={styles.removeBtn}
                onClick={() => dispatch(removeNominee(n.id))}
              >
                ×
              </button>
            )}

            <div className={styles.formGrid}>
              <label className={styles.field}>
                <span>Name of the Nominee</span>
                <input
                  value={n.name}
                  className={
                    hasNomineeError(n.id, "name") ? styles.inputError : ""
                  }
                  onChange={(e) => updateNominee(n.id, "name", e.target.value)}
                />
                {hasNomineeError(n.id, "name") && (
                  <p className={styles.fieldError}>Please fill this field</p>
                )}
              </label>

              <label className={styles.field}>
                <span>Address of the Nominee</span>
                <input
                  value={n.address}
                  className={
                    hasNomineeError(n.id, "address") ? styles.inputError : ""
                  }
                  onChange={(e) =>
                    updateNominee(n.id, "address", e.target.value)
                  }
                />
                {hasNomineeError(n.id, "address") && (
                  <p className={styles.fieldError}>Please fill this field</p>
                )}
              </label>

              <label className={styles.field}>
                <span>Nominee Relationship with the members</span>
                <select
                  value={n.relationship}
                  className={
                    hasNomineeError(n.id, "relationship")
                      ? styles.inputError
                      : ""
                  }
                  onChange={(e) =>
                    updateNominee(n.id, "relationship", e.target.value)
                  }
                >
                  <option value="">Select</option>
                  <option>Father</option>
                  <option>Mother</option>
                  <option>Spouse</option>
                </select>
                {hasNomineeError(n.id, "relationship") && (
                  <p className={styles.fieldError}>Please select an option</p>
                )}
              </label>

              <label className={styles.field}>
                <span>Share of Provident Fund towards the nominee</span>
                <select
                  value={n.share || ""}
                  disabled={totalShare >= 100 && !n.share}
                  className={
                    hasNomineeError(n.id, "share") ? styles.inputError : ""
                  }
                  onChange={(e) => updateNominee(n.id, "share", e.target.value)}
                >
                  <option value="">Select</option>
                  {getAvailableOptions(n.id).map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
                {hasNomineeError(n.id, "share") && (
                  <p className={styles.fieldError}>Please select a share</p>
                )}
              </label>

              <label className={styles.field}>
                <span>Nominee DOB</span>
                <input
                  type="date"
                  value={n.dob}
                  className={
                    hasNomineeError(n.id, "dob") ? styles.inputError : ""
                  }
                  onChange={(e) => updateNominee(n.id, "dob", e.target.value)}
                />
                {hasNomineeError(n.id, "dob") && (
                  <p className={styles.fieldError}>Please select a date</p>
                )}
              </label>

              <label className={styles.field}>
                <span>If the Nominee is a minor, name of the guardian</span>
                <input
                  value={n.guardian}
                  onChange={(e) =>
                    updateNominee(n.id, "guardian", e.target.value)
                  }
                />
              </label>
            </div>

            <div className={styles.uploadBlock}>
              {n.image ? (
                <div className={styles.previewRow}>
                  <img src={n.image.url} className={styles.imagePreview} />
                  <button
                    type="button"
                    className={styles.removeButton}
                    onClick={() => handleRemoveImage(n.id)}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <label className={styles.uploadButton}>
                  Upload file
                  <input
                    type="file"
                    className={styles.hiddenInput}
                    onChange={(e) => handleImage(n.id, e.target.files?.[0])}
                  />
                </label>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

export default EsiForm;
